import { useEffect, useRef, useState } from 'react'
import { useAssignableUsers } from '../../hooks/useReferenceData'
import type { AssignableUser } from '../../lib/api'

interface InlineAssigneeSelectProps {
  /** Plain label shown above the control ("Primary assignee", etc.). */
  label: string
  /** Current assignee user — may be null/undefined when unassigned. */
  currentUser?: { id: string; name: string } | null
  /** Other assignee id to exclude from the list (the "primary" excludes
   * the secondary and vice-versa so you can't assign the same user twice). */
  excludeUserId?: string | null
  /** Whether this select is actionable. Shows the name as plain text
   *  when false (e.g., user lacks assign permission). */
  canEdit: boolean
  /** Called with the new user id, or null when the user clears the slot.
   *  Auto-save happens inside this callback. */
  onChange: (userId: string | null) => void
  /** Optional placeholder when unassigned, e.g. "Unassigned". */
  placeholder?: string
  /** When true, a pending save is in progress — disable the control. */
  saving?: boolean
}

/**
 * Inline assignee dropdown. Clicking the current name opens a searchable
 * list of assignable users; selection auto-saves via onChange.
 *
 * No "Save" button, no form wrapper — this is meant to live in the
 * status sidebar where ops expects single-click reassignment.
 */
export function InlineAssigneeSelect({
  label,
  currentUser,
  excludeUserId,
  canEdit,
  onChange,
  placeholder = 'Unassigned',
  saving = false,
}: InlineAssigneeSelectProps) {
  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState('')
  const rootRef = useRef<HTMLDivElement | null>(null)

  // Lazy-load the user list — the query is cached for 5 minutes so
  // subsequent opens are instant.
  const { data: users, isLoading } = useAssignableUsers()

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open])

  const filtered: AssignableUser[] = (users ?? []).filter((u) => {
    if (excludeUserId && u.id === excludeUserId) return false
    if (!filter.trim()) return true
    const q = filter.trim().toLowerCase()
    return u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
  })

  const displayName = currentUser?.name || placeholder
  const isUnassigned = !currentUser

  return (
    <div ref={rootRef} className="relative">
      <div className="text-sm font-medium text-gray-500 mb-1">{label}</div>
      {canEdit ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          disabled={saving}
          className={[
            'w-full flex items-center justify-between px-3 py-2 rounded-md border text-sm text-left',
            'transition-colors duration-150',
            isUnassigned
              ? 'border-dashed border-gray-300 text-gray-400 hover:border-gray-400 hover:text-gray-600'
              : 'border-gray-200 text-gray-900 hover:border-gray-400 hover:bg-gray-50',
            saving ? 'opacity-60 cursor-wait' : 'cursor-pointer',
          ].join(' ')}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span>{saving ? 'Saving…' : displayName}</span>
          <svg
            className="h-4 w-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
      ) : (
        <div className="w-full px-3 py-2 rounded-md border border-transparent text-sm text-gray-900">
          {displayName}
        </div>
      )}

      {open && (
        <div className="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <input
              autoFocus
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search users…"
              className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <ul role="listbox" className="max-h-56 overflow-y-auto py-1">
            {/* Unassign option */}
            {!isUnassigned && (
              <li>
                <button
                  type="button"
                  onClick={() => {
                    onChange(null)
                    setOpen(false)
                    setFilter('')
                  }}
                  className="w-full text-left px-3 py-2 text-sm text-gray-500 italic hover:bg-gray-50"
                >
                  Unassign
                </button>
              </li>
            )}
            {isLoading && (
              <li className="px-3 py-2 text-sm text-gray-400">Loading users…</li>
            )}
            {!isLoading && filtered.length === 0 && (
              <li className="px-3 py-2 text-sm text-gray-400">No users match.</li>
            )}
            {filtered.map((user) => {
              const isCurrent = currentUser?.id === user.id
              return (
                <li key={user.id}>
                  <button
                    type="button"
                    onClick={() => {
                      if (!isCurrent) onChange(user.id)
                      setOpen(false)
                      setFilter('')
                    }}
                    className={[
                      'w-full text-left px-3 py-2 text-sm',
                      isCurrent
                        ? 'bg-blue-50 text-blue-700 font-medium cursor-default'
                        : 'text-gray-700 hover:bg-gray-50',
                    ].join(' ')}
                  >
                    <div>{user.name}</div>
                    <div className="text-xs text-gray-400">
                      {user.email} · {user.role.replace('_', ' ')}
                    </div>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}
